import React, { useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  Alert,
} from '@mui/material';
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
import * as yup from 'yup';
import dayjs from 'dayjs';
import ApprovalStepEditor from './ApprovalStepEditor';
import { LEAVE_TYPE_LABELS } from '../../containers/attendancemng/AttendMngContainer';

const DEFAULT_DAYS = {
  HALF_AM: 0.5,
  HALF_PM: 0.5,
  EARLY_LEAVE: 0.5,
};

const buildValidationSchema = () =>
  yup.object({
    leaveTyp: yup.string().required('유형을 선택하세요'),
    leaveDtSt: yup.string().required('시작일을 입력하세요'),
    leaveDtEd: yup.string().required('종료일을 입력하세요'),
    leaveDays: yup.number().min(0.5).required('일수를 입력하세요'),
    lineId: yup.string().nullable(),
  });

const normalizeApprovalSteps = (steps, currentUserUuid) => {
  return (steps || [])
    .filter(Boolean)
    .map((value) => value.trim())
    .filter((value) => value && value !== currentUserUuid);
};

const validateApprovalSteps = (steps, currentUserUuid) => {
  const normalized = normalizeApprovalSteps(steps, currentUserUuid);

  if (normalized.length === 0) {
    return '최소 1명의 결재자를 추가하세요.';
  }
  if (normalized.some((value) => value === currentUserUuid)) {
    return '본인을 결재자로 지정할 수 없습니다.';
  }
  if (new Set(normalized).size !== normalized.length) {
    return '같은 결재자를 중복 지정할 수 없습니다.';
  }

  return '';
};

const getLineStepValues = (line, currentUserUuid) => {
  const normalized = normalizeApprovalSteps(
    line?.steps?.map((step) => step.assigneeUsrUuid) || [],
    currentUserUuid,
  );
  return normalized.length > 0 ? normalized : [''];
};

const LeaveRequestModal = ({
  open,
  onClose,
  onSave,
  approvalLines = [],
  approvalLineLoading = false,
}) => {
  const { user } = useSelector(({ auth }) => ({ user: auth.user }));
  const currentUserUuid = useMemo(() => user?.usrUuid || '', [user]);

  const formik = useFormik({
    initialValues: {
      leaveTyp: 'ANNUAL',
      leaveDtSt: dayjs().format('YYYY-MM-DD'),
      leaveDtEd: dayjs().format('YYYY-MM-DD'),
      leaveDays: 1,
      rmk: '',
      lineId: '',
      approvalSteps: [''],
    },
    enableReinitialize: true,
    validationSchema: buildValidationSchema(),
    onSubmit: (values) => {
      const stepError = validateApprovalSteps(values.approvalSteps, currentUserUuid);
      if (stepError) {
        formik.setStatus({ approvalSteps: stepError });
        return;
      }

      onSave({
        leaveTyp: values.leaveTyp,
        leaveDtSt: values.leaveDtSt,
        leaveDtEd: values.leaveDtEd,
        leaveDays: Number(values.leaveDays),
        rmk: values.rmk,
        lineId: values.lineId || undefined,
        approverUuids: normalizeApprovalSteps(values.approvalSteps, currentUserUuid),
      });
    },
  });

  useEffect(() => {
    if (open) {
      formik.resetForm();
      formik.setStatus(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleLeaveTypChange = (e) => {
    const leaveTyp = e.target.value;
    formik.setFieldValue('leaveTyp', leaveTyp);
    if (DEFAULT_DAYS[leaveTyp]) {
      formik.setFieldValue('leaveDays', DEFAULT_DAYS[leaveTyp]);
    }
  };

  const handleDateChange = (field, value) => {
    formik.setFieldValue(field, value);
    const st = field === 'leaveDtSt' ? value : formik.values.leaveDtSt;
    const ed = field === 'leaveDtEd' ? value : formik.values.leaveDtEd;
    if (st && ed && !DEFAULT_DAYS[formik.values.leaveTyp]) {
      const diff = dayjs(ed).diff(dayjs(st), 'day') + 1;
      formik.setFieldValue('leaveDays', Math.max(1, diff));
    }
  };

  const handleApprovalLineChange = (e) => {
    const lineId = e.target.value;
    const selectedLine = approvalLines.find((line) => line.lineId === lineId);

    if (!selectedLine) {
      formik.setFieldValue('lineId', '');
      formik.setFieldValue('approvalSteps', ['']);
      return;
    }

    formik.setValues({
      ...formik.values,
      lineId,
      approvalSteps: getLineStepValues(selectedLine, currentUserUuid),
    });
    formik.setStatus(undefined);
  };

  const handleApprovalStepsChange = (steps) => {
    formik.setFieldValue('approvalSteps', steps);
    if (formik.values.lineId) {
      formik.setFieldValue('lineId', '');
    }
    if (formik.status?.approvalSteps) {
      formik.setStatus(undefined);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>휴가 신청</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          신청 즉시 approval v2 결재가 상신되며, 최종 승인 후에만 확정 근태로 반영됩니다. 저장된 템플릿은 결재 {'>'} 결재선 템플릿에서 관리하고, 여기서는 선택하거나 직접 결재 단계를 구성합니다.
        </Alert>
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              size="small"
              label="v2 결재선 템플릿"
              name="lineId"
              value={formik.values.lineId}
              onChange={handleApprovalLineChange}
              disabled={approvalLineLoading || approvalLines.length === 0}
              helperText={
                approvalLineLoading
                  ? 'v2 결재선 불러오는 중'
                  : approvalLines.length === 0
                    ? '저장된 템플릿이 없습니다. 아래에서 직접 단계 추가하세요.'
                    : '선택한 템플릿으로 결재 단계가 자동 반영됩니다. 이후 직접 수정하면 템플릿 선택은 해제됩니다.'
              }
            >
              <MenuItem value="">직접 선택</MenuItem>
              {approvalLines.map((line) => (
                <MenuItem key={line.lineId} value={line.lineId}>
                  {line.lineNm}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              size="small"
              label="휴가 유형 *"
              name="leaveTyp"
              value={formik.values.leaveTyp}
              onChange={handleLeaveTypChange}
              error={formik.touched.leaveTyp && Boolean(formik.errors.leaveTyp)}
              helperText={formik.touched.leaveTyp && formik.errors.leaveTyp}
            >
              {Object.entries(LEAVE_TYPE_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              size="small"
              label="일수 *"
              inputProps={{ min: 0.5, step: 0.5 }}
              name="leaveDays"
              value={formik.values.leaveDays}
              onChange={formik.handleChange}
              error={formik.touched.leaveDays && Boolean(formik.errors.leaveDays)}
              helperText={formik.touched.leaveDays && formik.errors.leaveDays}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="date"
              size="small"
              label="시작일 *"
              InputLabelProps={{ shrink: true }}
              name="leaveDtSt"
              value={formik.values.leaveDtSt}
              onChange={(e) => handleDateChange('leaveDtSt', e.target.value)}
              error={formik.touched.leaveDtSt && Boolean(formik.errors.leaveDtSt)}
              helperText={formik.touched.leaveDtSt && formik.errors.leaveDtSt}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="date"
              size="small"
              label="종료일 *"
              InputLabelProps={{ shrink: true }}
              name="leaveDtEd"
              value={formik.values.leaveDtEd}
              onChange={(e) => handleDateChange('leaveDtEd', e.target.value)}
              error={formik.touched.leaveDtEd && Boolean(formik.errors.leaveDtEd)}
              helperText={formik.touched.leaveDtEd && formik.errors.leaveDtEd}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              size="small"
              label="사유"
              name="rmk"
              value={formik.values.rmk}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <ApprovalStepEditor
              steps={formik.values.approvalSteps}
              onChange={handleApprovalStepsChange}
              currentUserUuid={currentUserUuid}
            />
            {formik.status?.approvalSteps && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {formik.status.approvalSteps}
              </Alert>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">취소</Button>
        <Button onClick={formik.handleSubmit} variant="contained">결재 상신</Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeaveRequestModal;
